import { useState } from 'react';
import { NewVatReturnPanel } from './components/NewVatReturnPanel.tsx';
import { VatReturnDraftView } from './components/VatReturnDraftView.tsx';
import { VatReturnRail } from './components/VatReturnRail.tsx';
import { VatReturnVersionView } from './components/VatReturnVersionView.tsx';
import type { VatDeclarationSnapshotSummary } from '../models.ts';
import type { VatReturnsState } from './useVatReturns.ts';
import { vatReturnToday } from './utils/index.ts';

export interface VatReturnTabProps {
    businessId: string;
    clientName: string;
    vatReturns: VatReturnsState;
    today: Date;
    onSaveDraft: () => void;
    onRequestApproval: (version: VatDeclarationSnapshotSummary) => void;
    onWithdrawApproval: (version: VatDeclarationSnapshotSummary, requestId: string) => void;
}

export function VatReturnTab({ businessId, clientName, vatReturns, today, onSaveDraft, onRequestApproval, onWithdrawApproval }: VatReturnTabProps) {
    const [isNewReturnOpen, setIsNewReturnOpen] = useState(false);
    const { selection, selectedPeriod, startDraft } = vatReturns;
    const isWaitingForSavedReturns = vatReturns.isLoadingSnapshots && selection === null;
    const selectedVersion = selection?.kind === 'version'
        ? selectedPeriod?.versions.find((version) => version.id === selection.snapshotId) ?? null
        : null;
    const isVersionMissingAfterLoadError = selection?.kind === 'version' && !selectedVersion && vatReturns.hasSnapshotsLoadError;
    const showNewReturn = !isWaitingForSavedReturns
        && (isNewReturnOpen || (!isVersionMissingAfterLoadError && (selection === null || selectedPeriod === null)));

    function generate(dateFrom: string, dateTo: string) {
        setIsNewReturnOpen(false);
        startDraft(dateFrom, dateTo);
    }

    function closeNewReturn<T extends unknown[]>(select: (...args: T) => void) {
        return (...args: T) => {
            setIsNewReturnOpen(false);
            select(...args);
        };
    }

    return (
        <div className="flex flex-col lg:flex-row">
            <VatReturnRail
                periods={vatReturns.periods}
                clientName={clientName}
                selection={selection}
                isNewReturnOpen={showNewReturn}
                isLoading={vatReturns.isLoadingSnapshots}
                hasLoadError={vatReturns.hasSnapshotsLoadError}
                onNewReturn={() => setIsNewReturnOpen(true)}
                onSelectPeriod={closeNewReturn(vatReturns.selectPeriod)}
                onSelectDraft={closeNewReturn(vatReturns.selectDraft)}
                onSelectVersion={closeNewReturn(vatReturns.selectVersion)}
            />
            <div className="min-w-0 grow px-4 py-6 lg:px-8">
                {showNewReturn && (
                    <NewVatReturnPanel
                        today={today}
                        periods={vatReturns.periods}
                        onGenerate={generate}
                        onCancel={selection !== null && selectedPeriod !== null ? () => setIsNewReturnOpen(false) : undefined}
                    />
                )}
                {!showNewReturn && selectedPeriod && selection?.kind === 'draft' && (
                    <VatReturnDraftView
                        businessId={businessId}
                        period={selectedPeriod}
                        declaration={vatReturns.draft.data}
                        isComputing={vatReturns.draft.isLoading}
                        isRefreshing={vatReturns.draft.isRefreshing}
                        hasComputeError={vatReturns.draft.isError}
                        today={vatReturnToday(today)}
                        isLoadingApprovalStatus={vatReturns.isLoadingSnapshots}
                        hasApprovalStatusLoadError={vatReturns.hasSnapshotsLoadError}
                        isSaving={vatReturns.isSaving}
                        onSave={onSaveDraft}
                        onRegenerate={() => startDraft(selectedPeriod.periodStart, selectedPeriod.periodEnd)}
                        onDiscard={() => vatReturns.discardDraft(selectedPeriod.key)}
                        onSelectVersion={(snapshotId) => vatReturns.selectVersion(selectedPeriod.key, snapshotId)}
                    />
                )}
                {!showNewReturn && isVersionMissingAfterLoadError && (
                    <p role="alert" className="text-sm text-red-700">De versies konden niet worden vernieuwd. Probeer het opnieuw.</p>
                )}
                {!showNewReturn && selectedPeriod && selectedVersion && (
                    <VatReturnVersionView
                        businessId={businessId}
                        clientName={clientName}
                        period={selectedPeriod}
                        version={selectedVersion}
                        snapshot={vatReturns.snapshot.data}
                        isLoading={vatReturns.snapshot.isLoading}
                        hasLoadError={vatReturns.snapshot.isError}
                        isRequestingApproval={vatReturns.isRequestingApproval}
                        isWithdrawingApproval={vatReturns.isWithdrawingApproval}
                        onSelectVersion={(snapshotId) => vatReturns.selectVersion(selectedPeriod.key, snapshotId)}
                        onRequestApproval={() => onRequestApproval(selectedVersion)}
                        onWithdrawApproval={(requestId) => onWithdrawApproval(selectedVersion, requestId)}
                    />
                )}
            </div>
        </div>
    );
}
